import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	OnDestroy,
	computed,
	inject,
	signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
	deriveSetupRequirement,
	formatMonthlyPrice,
	sipAccountIsOnOffer,
} from '../shared/helpers/guided-setup.helper';
import {
	GUIDED_SETUP_DIALOG_WIDTH,
	GUIDED_SETUP_HEADING_ID,
	GUIDED_SETUP_PANEL_CLASS,
	GuidedSetupDialog,
	GuidedSetupResult,
	languageTagFor,
} from '../guided-setup/guided-setup-dialog';
import { isMockMode } from '../shared/testing/mock-mode';
import { GuidedSetupStore } from '../guided-setup/guided-setup.store';
import { AuthTokenStore } from '../shared/services/auth-token.store';
import { FeatureService } from '../shared/services/feature.service';
import { LoggingService } from '../shared/services/logging.service';
import { LOG_EVENTS } from '../shared/services/log-events';
import {
	AiInstruction,
	BusinessHourDTO,
	CallbackForm,
	CallbackFormData,
	CallbackOnCallNoteRequestDTO,
	DailyBusinessHour,
	ExistingOutboundTaskDTO,
	GenderEnum,
	normalCallbackTimes,
	PrefixData,
	ScheduleFormGroup,
	VoiceBotMode,
} from '../shared/models/callback-on-callnote.model';
import { IconEnum, ImgEnum } from '../shared/models/assets.model';
import { I18nKeys } from '../shared/models/i18n.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CallbackOnCallNote } from '../shared/models/constant.model';
import { CustomValidator } from '../shared/validators/custom-validator';
import { SCHEDULE_OPTIONS } from '../shared/constants/schedule-options';
import { ScheduleOption, ScheduleOptionValueEnum } from '../shared/models/schedule-option.model';
import { MatDialog } from '@angular/material/dialog';
import { CallbackService } from '../shared/services/callback.service';
import { DataService } from '../shared/services/data.service';
import { BusinessHoursService } from '../shared/services/business-hours.service';
import { PriceInfoDialog } from '../price-info-dialog/price-info-dialog';
import { I18nPipe } from '../shared/pipe/i18n-pipe';
import {
	getDateOfDateAndTime,
	nextRoundedPossibleTime,
	timeIsOutsideOfTimeRangeOrWeekendDay,
} from '../shared/helpers/date-time.helper';
import dayjs from 'dayjs';

@Component({
	selector: 'app-callback-on-callnote',
	standalone: false,
	templateUrl: './callback-on-callnote.html',
	styleUrl: './callback-on-callnote.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallbackOnCallnote implements OnDestroy {
	@HostBinding('class.app-callback-on-callnote') default = true;
	private subscriptions = new Subscription();
	private callbackOnCallNote?: CallbackOnCallNote;
	private readonly dialog: MatDialog = inject(MatDialog);
	private readonly callbackService: CallbackService = inject(CallbackService);
	private readonly dataService: DataService = inject(DataService);
	private readonly businessHourService: BusinessHoursService = inject(BusinessHoursService);
	protected readonly ICONS = IconEnum;
	protected readonly IMG = ImgEnum;
	public I18N = I18nKeys;
	public readonly scheduleOptions: ScheduleOption[] = SCHEDULE_OPTIONS;
	public callbackQuestion = signal('');
	public customSuggestionCtrl: FormControl<string> = new FormControl('', {
		nonNullable: true,
		validators: [Validators.required],
	});
	public aiSuggestionCtrl: FormControl<number | null> = new FormControl();
	public selectedScheduleOptionCtrl: FormControl<ScheduleOptionValueEnum> =
		new FormControl<ScheduleOptionValueEnum>(ScheduleOptionValueEnum.IMMEDIATE, {
			nonNullable: true,
			validators: [Validators.required],
		});
	public timeCtrl: FormControl<Date> = new FormControl(new Date(), { nonNullable: true });
	public dateCtrl: FormControl<Date> = new FormControl(new Date(), { nonNullable: true });
	public scheduleFormGroup: FormGroup<ScheduleFormGroup> = new FormGroup({
		time: this.timeCtrl,
		date: this.dateCtrl,
	});
	public outboundOutgoingNumberCtrl: FormControl<string> = new FormControl('', {
		nonNullable: true,
	});
	public requestContactForBookingNumberCtrl: FormControl<boolean> = new FormControl(false, {
		nonNullable: true,
	});
	public voicemailAllowedCtrl: FormControl<boolean> = new FormControl(false, { nonNullable: true });
	public voiceBotModeCtrl: FormControl<VoiceBotMode | null> = new FormControl<VoiceBotMode | null>(
		null,
	);
	public callbackFormGroup: FormGroup<CallbackForm> = new FormGroup({
		customSuggestion: this.customSuggestionCtrl,
		aiSuggestion: this.aiSuggestionCtrl,
		selectedScheduleOption: this.selectedScheduleOptionCtrl,
		schedule: this.scheduleFormGroup,
		outboundOutgoingNumber: this.outboundOutgoingNumberCtrl,
		requestContactForBookingNumber: this.requestContactForBookingNumberCtrl,
		voicemailAllowed: this.voicemailAllowedCtrl,
		voiceBotMode: this.voiceBotModeCtrl,
	});
	public suggestions: AiInstruction[] = [];
	public isEditMode = signal(false);
	public orderedDailyBusinessHours: (null | DailyBusinessHour)[] = [];
	public minStartTime = signal('00:00');
	public maxEndTime = signal('23:59');
	public selectedSuggestion = signal<string | undefined>(undefined);
	public businessHoursTooltip: string = '';
	public businessHoursInformation = signal<string[]>([]);
	public MAX_LENGTH_CUSTOM_SUGGESTION = 2000;
	public dateFilter = (date: Date | null): boolean => {
		if (!date) {
			return true;
		}
		if (!this.orderedDailyBusinessHours[date.getDay()]) {
			return false;
		}

		const today = new Date(new Date().setHours(0, 0, 0, 0));
		return date >= today;
	};
	public callbackCreated = false;
	public callbackCreationFailed = false;
	public nighttimeWarning = signal(false);
	public soonDate = signal<string | undefined>(undefined);
	public scheduleOption = ScheduleOptionValueEnum;
	public existingOutboundTask = signal<ExistingOutboundTaskDTO | null>(null);
	public hasExistingTask = computed(() => !!this.existingOutboundTask());

	/**
	 * PS-6495 — the guided setup's surviving state. Read here only to disable the call button while a
	 * run is open; the run itself belongs to the dialog.
	 */
	private readonly setupStore: GuidedSetupStore = inject(GuidedSetupStore);
	private readonly tokenStore: AuthTokenStore = inject(AuthTokenStore);

	/**
	 * True while the dialog owns the workflow. The overlay already blocks pointer events, so this is
	 * not about a human mis-clicking — it is that the button must BE disabled, not merely
	 * unreachable: found because a script could still click it straight through the backdrop and
	 * start a second run.
	 */
	public readonly setupDialogOpen = signal(false);

	/**
	 * L2 (PS-6495 code review 2): true while the callback request is out. After a setup `endRun()`
	 * has already reset the run, so a second press in that round trip would see a blocked payload
	 * and reopen the setup - or create a second task. Both entry points refuse while this is set.
	 */
	public readonly callbackInFlight = signal(false);
	private readonly logger: LoggingService = inject(LoggingService);
	public existingTaskTranslationKey = signal<string>('');
	public timeZone: string = '';
	// EB-4016: "who handles the call" (AI vs Agent). Backend-driven — shown only in the voicebot
	// case, i.e. when BOTH a default and non-empty options are delivered.
	public voiceBotModeOptions = signal<VoiceBotMode[]>([]);
	public defaultVoiceBotMode = signal<VoiceBotMode | null>(null);
	public showVoiceBotMode = computed(
		() => this.voiceBotModeOptions().length > 0 && this.defaultVoiceBotMode() !== null,
	);

	constructor(
		private readonly i18nPipe: I18nPipe,
		private readonly featureService: FeatureService,
	) {
		this.initSubscription();
		this.setupForm();
		this.updateTimeWarning(nextRoundedPossibleTime(), false);
	}

	public triggerOutboundCall() {
		if (!this.callbackFormGroup.valid) {
			return;
		}

		if (this.callbackInFlight()) {
			return;
		}

		if (this.hasExistingTask()) {
			return;
		}

		/**
		 * Decided on what the payload reports, not only on the number field: a customer with no
		 * SIP-Account but a number already set would otherwise get a task that cannot be placed.
		 * Not blocked means the payload carries a number, which `setupOutboundOutgoingNumber()` has
		 * already put into the form, so the callback can be created directly.
		 *
		 * @ticket PS-6495
		 */
		if (this.setupRequirement.isBlocked) {
			this.openGuidedSetup();
			return;
		}

		this.createCallback();
	}

	/**
	 * A GETTER, deliberately not a `computed()`. `callbackOnCallNote` is a plain property assigned
	 * from a subscription, not a signal — so a computed over it would have no dependency to
	 * invalidate on, would evaluate exactly once and cache that answer forever. It happens to work
	 * today only because the first read is the button click, which is always after the payload
	 * arrives. That is luck, not design.
	 */
	public get setupRequirement() {
		return deriveSetupRequirement(this.setupSignals);
	}

	public get setupSignals() {
		return {
			hasSipNumber: this.callbackOnCallNote?.callbackOnCallNoteDTO.hasSipNumber,
			hasSipExtension: this.callbackOnCallNote?.callbackOnCallNoteDTO.hasSipExtension,
			outboundOutgoingPhoneNumber:
				this.callbackOnCallNote?.callbackOnCallNoteDTO.outboundOutgoingPhoneNumber,
			/**
			 * Price and currency together decide whether the extension can be ordered at all, not
			 * just what it costs - see `SetupSignals.sipExtensionPrice` and `sipAccountIsOnOffer`.
			 */
			sipExtensionPrice: this.callbackOnCallNote?.callbackOnCallNoteDTO.sipExtensionPrice,
			currency: this.callbackOnCallNote?.callbackOnCallNoteDTO.currency,
			cliVerificationStatus: this.callbackOnCallNote?.callbackOnCallNoteDTO.cliVerificationStatus,
			cliVerificationMethod: this.callbackOnCallNote?.callbackOnCallNoteDTO.cliVerificationMethod,
		};
	}

	/**
	 * The price as the customer reads it, or null when no figure may be shown. The locale is the
	 * PAGE's language - the callnote's `languageId`, the same one that picks the dialog's copy -
	 * not the browser's: a German screen showing "€4.90" because the browser is set to en-US
	 * reads as a different product (PR review, MI3). The currency is per brand and comes from the
	 * payload. An id the platform does not map falls back to de-DE, the language this flow ships in.
	 */
	public get setupMonthlyCost(): string | null {
		const dto = this.callbackOnCallNote?.callbackOnCallNoteDTO;
		return formatMonthlyPrice(
			dto?.sipExtensionPrice,
			dto?.currency,
			languageTagFor(dto?.languageId) ?? 'de-DE',
		);
	}

	/**
	 * False when the plan does not carry the extension — see the DTO for why that is not "unknown" —
	 * or when no price can be shown for it (M3): the same predicate the routing uses.
	 */
	public get setupSipOnOffer(): boolean {
		const dto = this.callbackOnCallNote?.callbackOnCallNoteDTO;
		return sipAccountIsOnOffer(dto?.sipExtensionPrice, dto?.currency);
	}

	public get setupAvailableNumbers() {
		return this.callbackOnCallNote?.callbackOnCallNoteDTO.availableOutgoingNumbers ?? [];
	}

	/**
	 * The dialog is the frame of the workflow from here on. Torsten, 2026-09-04: the call button in
	 * the background is out of play while it is open — Material's overlay blocks it and
	 * `setupDialogOpen()` disables it.
	 *
	 * Once the dialog is CLOSED with a run still alive the button is deliberately enabled again: it
	 * is the only way back in, and it says so — `setupInProgress` swaps its label to "Einrichtung
	 * fortsetzen". Pressing it resumes where the run left off.
	 */
	private openGuidedSetup(): void {
		const ref = this.dialog.open<GuidedSetupDialog, unknown, GuidedSetupResult>(GuidedSetupDialog, {
			data: {
				signals: this.setupSignals,
				availableNumbers: this.setupAvailableNumbers,
				monthlyCost: this.setupMonthlyCost,
				sipOnOffer: this.setupSipOnOffer,
				expectedPhoneServiceId: this.callbackOnCallNote?.callbackOnCallNoteDTO.phoneServiceId ?? null,
				languageId: this.callbackOnCallNote?.callbackOnCallNoteDTO.languageId ?? null,
			},
			// M8: the dialog is announced by the current step's heading - see the constant.
			ariaLabelledBy: GUIDED_SETUP_HEADING_ID,
			// No panelClass padding fight: the component brings its own header/body/footer.
			panelClass: GUIDED_SETUP_PANEL_CLASS,
			// A DEFINITE width, not a minimum. Two reasons, both measured:
			//
			// 1. `minWidth` left the panel content-sized, so anything wider than 640px grew it. The
			//    consent warning did exactly that - clicking "Bestellung bestätigen" with the terms
			//    unticked jumped the dialog from 640px to 795px, because the note has no max-width
			//    and its max-content line set the panel's preferred width. A dialog that widens when
			//    an error appears reads as something breaking (Torsten, 2026-10-02).
			// 2. `min-width` beats `max-width` in CSS, so `maxWidth: 92vw` never applied: on a 375px
			//    phone the panel stayed 640px and ran off the right edge. This page is opened from an
			//    email link, which is mostly a phone.
			//
			// With `width` the panel is 640px on desktop and 92vw below that, and content wraps
			// instead of pushing. The original intent - not resizing between steps - is better served
			// by a fixed width than by a floor.
			// 760, raised from 640 on 2026-10-07: the order step grew with the consent block and was
			// paying for it in height. Width is the cheaper axis here — the panel has room sideways
			// and none downwards. `.legal`'s column cap was raised with it, or the extra width would
			// have been absorbed by the margin instead of shortening the text.
			//
			// A second, VIEWPORT-keyed tier lives in styles.scss: at >= 1000px wide and <= 960px tall
			// the pane goes to 1060px, so the order step can run its two halves side by side instead of
			// stacking them. Still derived from the viewport and never from the content, still the same
			// on every phase, still shrinkable by the 92vw cap below. The breakpoints are tokens in
			// shared/styles/variables.scss. Change one, read the other.
			width: GUIDED_SETUP_DIALOG_WIDTH,
			// Still the guard below ~826px viewport: `maxWidth` wins over `width`, so the panel
			// shrinks with the screen. It must stay a maxWidth and never become a minWidth — that
			// swap is what used to push the dialog off the right edge of a phone.
			maxWidth: '92vw',
			/**
			 * `dvh` so the footer cannot sit under a mobile URL bar (L13). guided-setup.scss carries a
			 * `vh` fallback on the component itself for browsers without dynamic viewport units.
			 */
			maxHeight: '92dvh',
			// The backdrop and Escape no longer close it (Torsten, 2026-09-11). This run concludes a
			// paid contract, so leaving it is a decision the customer makes with a button, not by
			// clicking slightly off target. The header's close button still calls suspend(), which
			// keeps the run alive for the next open.
			disableClose: true,
			autoFocus: false,
		});

		this.setupDialogOpen.set(true);

		ref.afterClosed().subscribe((result) => {
			this.setupDialogOpen.set(false);
			// `undefined` can still arrive (a programmatic close), and it means the same as 'suspended':
			// closed, run kept. The backdrop and Escape no longer produce it — see `disableClose` above.
			if (!result || result.outcome === 'suspended') {
				return;
			}
			if (result.outcome === 'ready') {
				this.onSetupReady(result.outgoingNumber);
				return;
			}
			if (result.outcome === 'failed') {
				this.onSetupFailed(result.reason);
				return;
			}
			if (result.outcome === 'contactService') {
				this.onSetupContactService();
				return;
			}
			this.onSetupDismissed();
		});
	}

	/**
	 * True once a run has started and until it ends. Kept for the template so a suspended run is
	 * visibly resumable rather than silently restartable.
	 */
	public get setupInProgress(): boolean {
		return this.setupStore.active();
	}

	/**
	 * Ends the run: the guided-setup state AND the customer's bearer.
	 *
	 * The token is the half that is easy to forget, because nothing visible depends on it. It is
	 * held in a root-provided store, so without this it survives for the life of the tab — on a page
	 * reached from an emailed link, where `AuthTokenStore`'s own D3 note names the shared or family
	 * device as the normal case. The visible symptom is smaller and was the giveaway: a second run
	 * opened with a green check already on "Anmelden", because the rail reads `hasToken`.
	 *
	 * Not called on 'suspended' — that run is still going and the customer must not have to sign in
	 * again to resume it.
	 */
	private endRun(): void {
		this.setupStore.reset();
		this.tokenStore.clear();
	}

	/** Setup finished: the number is settled, so the callback is created without a further click. */
	public onSetupReady(outgoingNumber: string): void {
		this.endRun();
		this.outboundOutgoingNumberCtrl.setValue(outgoingNumber);
		this.requestContactForBookingNumberCtrl.setValue(false);
		this.createCallback();
	}

	/**
	 * "Not now" — the customer gave up. This only ends the run; no task is created. The support path
	 * is a separate outcome (`contactService`, see `onSetupContactService`), offered on the screens
	 * where the service actually has to follow up.
	 */
	public onSetupDismissed(): void {
		this.endRun();
	}

	/**
	 * The service has to follow up — a refused or unconfirmed booking, or a sign-in problem the
	 * customer chose to hand over. Creates the callback the way the old `contact_us` choice did:
	 * no outgoing number, `requestContactForBookingNumber` set. The task is created and the service
	 * starts it manually once the number is set up; no `outgoingNumberNotProvided` field or
	 * `ConcernCreator` is needed for that.
	 */
	public onSetupContactService(): void {
		this.endRun();
		this.requestContactForBookingNumberCtrl.setValue(true);
		this.createCallback();
	}

	/** Logged BEFORE `endRun()`: the log bridge needs the bearer that ending the run clears. */
	public onSetupFailed(reason: string): void {
		this.logger.error(LOG_EVENTS.failed, {
			stage: 'run',
			reason,
			phoneServiceId: this.setupStore.phoneServiceId(),
		});
		this.endRun();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	toggleView() {
		this.isEditMode.set(!this.isEditMode());
	}

	// EB-4016: map a voice-bot mode to its i18n key (labels delivered in the translations block,
	// e.g. ALWAYS_AI -> 'voice_bot_mode_always_ai'). Keeps the mapping BE-value-driven.
	protected voiceBotModeLabelKey(mode: VoiceBotMode): string {
		return `voice_bot_mode_${mode.toLowerCase()}`;
	}

	showInfos() {
		const dialogRef = this.dialog.open(PriceInfoDialog, {
			data: {
				someVar: 'someValue',
			},
			minWidth: '300px',
			maxWidth: '500px',
			minHeight: '300px',
			maxHeight: '80vh',
			autoFocus: false,
		});
	}

	private updateTimeWarning(date: Date, isFixedDate = true) {
		let soonDate = undefined;
		if (!isFixedDate) {
			date = this.businessHourService.alignDateOnBusinessHoursIfRequired(date);
			soonDate = dayjs(date).format('DD.MM.YYYY HH:mm');
		}
		this.soonDate.set(soonDate);

		// in: time - return time - possible adjusted based on working hours.
		this.nighttimeWarning.set(
			timeIsOutsideOfTimeRangeOrWeekendDay(
				date,
				normalCallbackTimes.startTime,
				normalCallbackTimes.endTime,
			),
		);
	}

	private initSubscription() {
		this.subscriptions.add(
			this.featureService.callbackOnCallNoteData$.subscribe((callbackOnCallNote) => {
				this.callbackOnCallNote = callbackOnCallNote;
				this.timeZone = callbackOnCallNote.callbackOnCallNoteDTO.timeZone;
				this.suggestions = callbackOnCallNote.callbackOnCallNoteDTO.instructionSuggestions;
				/*
				 * TEMPORARY (PS-6495 mock harness) — picks the first suggestion so a mocked run reaches
				 * the guided setup in one click instead of three. Every scenario URL otherwise starts
				 * with the same manual chore, which is a poor use of a reviewer's attention.
				 *
				 * Gated on mock mode, which is itself gated on a test host (`URL_SUB_DOMAIN`), so a
				 * real customer never arrives with an answer they did not give — that answer is what
				 * we tell the person being called.
				 */
				if (isMockMode() && this.suggestions.length > 0 && !this.aiSuggestionCtrl.value) {
					// Both controls explicitly. The radio's `valueChanges` normally fills the text field,
					// but that subscription is wired in `setupForm()` and this runs from the data
					// subscription — relying on the order between them left the field empty and the form
					// invalid, which is exactly the click this is meant to save.
					this.aiSuggestionCtrl.setValue(this.suggestions[0].id, { emitEvent: false });
					this.customSuggestionCtrl.setValue(this.suggestions[0].suggestion, {
						emitEvent: false,
					});
				}
				// EB-4016: hydrate the "who handles the call" selector from backend-provided options + default.
				this.voiceBotModeOptions.set(
					callbackOnCallNote.callbackOnCallNoteDTO.voiceBotModeOptions ?? [],
				);
				this.defaultVoiceBotMode.set(
					callbackOnCallNote.callbackOnCallNoteDTO.defaultVoiceBotMode ?? null,
				);
				this.voiceBotModeCtrl.setValue(this.defaultVoiceBotMode());
				if (this.callbackOnCallNote?.preselectedSuggestion) {
					const matchingSuggestion: AiInstruction | undefined = (this.suggestions || []).find(
						(s) => s.id === this.callbackOnCallNote?.preselectedSuggestion,
					);

					if (matchingSuggestion) {
						this.selectedSuggestion.set(matchingSuggestion?.suggestion);
						this.aiSuggestionCtrl.setValue(matchingSuggestion.id);
						this.customSuggestionCtrl.setValue(this.selectedSuggestion() || '');
					}
				}

				if (!this.selectedSuggestion()) {
					this.isEditMode.set(true);
				}

				if (this.callbackOnCallNote.priceInfo) {
					this.showInfos();
				}

				this.setCallbackQuestion();
				this.businessHourService.timezone = callbackOnCallNote.callbackOnCallNoteDTO.timeZone;

				this.setupScheduleFormGroup(callbackOnCallNote.callbackOnCallNoteDTO.businessHours);
				this.setupScheduleFormSubscriptions();
				this.setupOutboundOutgoingNumber();
				this.setupExistingOutboundTask(
					callbackOnCallNote.callbackOnCallNoteDTO.existingOutboundTask,
				);
			}),
		);
	}

	private setCallbackQuestion() {
		const callerDTO = this.callbackOnCallNote?.callbackOnCallNoteDTO?.callNote?.caller;
		if (!callerDTO || !callerDTO) {
			// TODO handle no caller data -> error
			console.error('No caller information are available.');
			return;
		}
		let caller = `${this.i18nPipe.translate(this.I18N.THE_CALLER)}`;
		const prefixData: PrefixData = {
			male: this.i18nPipe.translate(this.I18N.PREFIX_MALE),
			female: this.i18nPipe.translate(this.I18N.PREFIX_MALE),
			neutral: this.i18nPipe.translate(this.I18N.PREFIX_NEUTRAL),
		};
		let prefix = '';
		if (!!callerDTO.firstname || !!callerDTO.lastname) {
			const salutation = callerDTO.salutation;
			caller = !!salutation ? (salutation === 'Herr' ? 'Herrn ' : `${salutation} `) : '';
			caller = caller.concat(!!callerDTO.firstname ? `${callerDTO.firstname}` : '');
			caller = caller.concat(!!callerDTO.firstname && !!callerDTO.lastname ? ' ' : '');
			caller = caller.concat(!!callerDTO.lastname ? callerDTO.lastname : '');
			prefix = this.setPrefix(callerDTO.gender, prefixData);
		}
		let callbackQuestion = `${this.i18nPipe.translate(this.I18N.CALLBACK_QUESTION)}`;
		callbackQuestion = callbackQuestion.replace(/%PREFIX%/, prefix);
		callbackQuestion = callbackQuestion.replace(/%CALLER%/, caller);
		this.callbackQuestion.set(callbackQuestion);
	}

	private setPrefix(gender: GenderEnum | null, prefixData: PrefixData) {
		let prefix = prefixData.neutral;
		if (gender !== null) {
			prefix = gender === GenderEnum.MALE ? prefixData.male : prefixData.female;
		}
		return prefix.length > 0 ? `${prefix} ` : ``;
	}

	private setupForm() {
		this.aiSuggestionCtrl.valueChanges.subscribe((suggestionId) => {
			if (suggestionId) {
				this.customSuggestionCtrl.setValue(
					this.suggestions.find((s) => s.id === suggestionId)?.suggestion || '',
					{ emitEvent: false },
				);
			}
		});

		this.scheduleFormGroup.valueChanges.subscribe((a) => {
			this.updateTimeWarning(getDateOfDateAndTime(this.dateCtrl.value, this.timeCtrl.value));
		});

		this.customSuggestionCtrl.valueChanges.subscribe((txt) => {
			if (this.aiSuggestionCtrl.value) {
				this.aiSuggestionCtrl.setValue(null);
			}
		});
	}

	private updateScheduleFormValidators(scheduleOption: ScheduleOptionValueEnum | null) {
		if (scheduleOption === ScheduleOptionValueEnum.SPECIFIC_DATE_TIME) {
			this.scheduleFormGroup.setValidators([
				CustomValidator.dateTimeValidator(this.orderedDailyBusinessHours),
			]);
		} else {
			this.updateTimeWarning(nextRoundedPossibleTime(), false);
			this.timeCtrl.clearValidators();
			this.dateCtrl.clearValidators();
			this.scheduleFormGroup.clearValidators();
		}

		this.callbackFormGroup.updateValueAndValidity();
	}

	private setMinMaxTime(date: Date | null) {
		if (date) {
			const day = date.getDay();
			this.minStartTime.set(this.orderedDailyBusinessHours[day]?.startTime || '00:00');
			this.maxEndTime.set(this.orderedDailyBusinessHours[day]?.endTime || '23:59');
		}
	}

	private setupScheduleFormGroup(businessHours: BusinessHourDTO[]) {
		const businessHoursOverview = this.businessHourService.getBusinessHoursOverview(businessHours);
		this.orderedDailyBusinessHours = businessHoursOverview.dailyBusinessHour;
		this.businessHoursInformation.set(businessHoursOverview.optimizedInformation);
		this.businessHoursTooltip = businessHoursOverview.tooltip;
		const nextValidDate = this.businessHourService.getNextValidDateAndTime(
			this.orderedDailyBusinessHours,
		);

		this.dateCtrl.setValue(nextValidDate);
		this.timeCtrl.setValue(nextValidDate);

		this.setMinMaxTime(nextValidDate);
	}

	private setupScheduleFormSubscriptions() {
		this.subscriptions.add(
			this.dateCtrl.valueChanges.subscribe((date) => {
				this.setMinMaxTime(date);
			}),
		);

		this.subscriptions.add(
			this.selectedScheduleOptionCtrl.valueChanges.subscribe((value) => {
				this.updateScheduleFormValidators(value as ScheduleOptionValueEnum);
			}),
		);
	}

	private createCallback() {
		if (this.hasExistingTask() || this.callbackInFlight()) {
			return;
		}
		this.callbackInFlight.set(true);
		const formData: CallbackFormData = this.callbackFormGroup.getRawValue();
		const payload: CallbackOnCallNoteRequestDTO =
			this.callbackService.prepareCallbackRequestPayload(
				formData,
				this.callbackOnCallNote?.callbackOnCallNoteDTO.callNote.caller!,
			);
		this.dataService.createCallbackOnCallNote$(payload, this.callbackOnCallNote?.uid!).subscribe({
			next: (res) => {
				this.callbackCreated = true;
				this.callbackFormGroup.disable();
				this.callbackInFlight.set(false);
			},
			error: (err) => {
				this.callbackCreationFailed = true;
				this.callbackFormGroup.disable();
				this.callbackInFlight.set(false);
			},
		});
	}

	private setupOutboundOutgoingNumber() {
		if (this.callbackOnCallNote?.callbackOnCallNoteDTO.outboundOutgoingPhoneNumber) {
			this.outboundOutgoingNumberCtrl.setValue(
				this.callbackOnCallNote.callbackOnCallNoteDTO.outboundOutgoingPhoneNumber,
			);
		}
	}

	private setupExistingOutboundTask(task: ExistingOutboundTaskDTO | null) {
		this.existingOutboundTask.set(task);
		if (!task) {
			this.existingTaskTranslationKey.set('');
			return;
		}

		const status = task.status?.toUpperCase();
		const translationKey =
			status === 'IN_PROGRESS'
				? this.I18N.EXISTING_OUTBOUND_TASK_IN_PROGRESS_WARN
				: this.I18N.EXISTING_OUTBOUND_TASK_SCHEDULED_WARN;
		this.existingTaskTranslationKey.set(translationKey);
	}
}
